import { useState } from "react";
import { UserPlus, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";

const PROGRAMS = ["Playgroup", "Nursery", "LKG", "UKG", "Day Care"];
const initialState = {
  parent_name: "", phone: "", email: "", child_name: "", child_age: "", program: "", message: "",
};

const WalkInDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.parent_name || !form.phone) {
      toast.error("Parent name and phone are required.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      await api.post("/staff/inquiries", payload);
      toast.success("Walk-in inquiry recorded.");
      setForm(initialState);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Hourly walk-in limit reached. Please wait a bit before adding more.");
      } else {
        const msg = err?.response?.data?.detail || "Could not save walk-in.";
        toast.error(typeof msg === "string" ? msg : "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="add-walkin-btn"
          className="rounded-full bg-ark-teal hover:bg-ark-tealDeep text-white font-display font-semibold"
        >
          <UserPlus className="h-4 w-4 mr-2" /> Add Walk-in
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg rounded-4xl max-h-[90vh] overflow-y-auto" data-testid="walkin-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Record Walk-in Inquiry</DialogTitle>
          <DialogDescription>
            Log an inquiry for a parent visiting the school. Name and phone are required.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 pt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Parent's Name *</Label>
              <Input
                data-testid="walkin-parent-name"
                value={form.parent_name}
                onChange={(e) => update("parent_name", e.target.value)}
                placeholder="Parent name"
                className="rounded-2xl h-11 border-slate-200"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Phone *</Label>
              <Input
                data-testid="walkin-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+91 …"
                className="rounded-2xl h-11 border-slate-200"
                required
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Email</Label>
              <Input
                data-testid="walkin-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="optional"
                className="rounded-2xl h-11 border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Program</Label>
              <Select value={form.program} onValueChange={(v) => update("program", v)}>
                <SelectTrigger data-testid="walkin-program" className="rounded-2xl h-11 border-slate-200">
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent>
                  {PROGRAMS.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Child's Name</Label>
              <Input
                data-testid="walkin-child-name"
                value={form.child_name}
                onChange={(e) => update("child_name", e.target.value)}
                className="rounded-2xl h-11 border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-slate-700">Child's Age</Label>
              <Input
                data-testid="walkin-child-age"
                value={form.child_age}
                onChange={(e) => update("child_age", e.target.value)}
                placeholder="e.g. 3 years"
                className="rounded-2xl h-11 border-slate-200"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="font-semibold text-slate-700">Notes</Label>
            <Textarea
              data-testid="walkin-message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              rows={3}
              className="rounded-2xl border-slate-200 resize-none"
            />
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={loading}
              data-testid="walkin-submit-btn"
              className="rounded-full bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold w-full sm:w-auto"
            >
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : "Save Walk-in"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WalkInDialog;
